"use client";
import {
  EventScheduledEvent,
  PopupModal,
  useCalendlyEventListener,
} from "react-calendly";

export interface ClientUserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}

interface CalendlyPopupProps {
  url: string;
  prefillUserData?: ClientUserData;
  open: boolean;
  setOpen: (v: boolean) => void;
  onEventScheduled?: (evUri: string) => void;
}

enum EventName {
  CALENDLY_OPENED = "calendly_opened",
  DATETIME_SELECTED = "datetime_selected",
  APPT_SUBMITTED = "appt_submitted",
}

const YARDZEN_STYLE_DEFAULTS = {
  backgroundColor: "f3f3ed",
  primaryColor: "4e5a44",
  textColor: "4d5055",
} as const;

/**
 * Builds pageSettings by extracting style params from the URL if present,
 * otherwise falling back to Yardzen brand defaults.
 */
function buildPageSettings(url: string) {
  let urlBackgroundColor: string | null = null;
  let urlPrimaryColor: string | null = null;
  let urlTextColor: string | null = null;

  try {
    const parsed = new URL(url);
    urlBackgroundColor = parsed.searchParams.get("background_color");
    urlPrimaryColor = parsed.searchParams.get("primary_color");
    urlTextColor = parsed.searchParams.get("text_color");
  } catch {
    // Invalid URL, use all defaults
  }

  return {
    backgroundColor:
      urlBackgroundColor ?? YARDZEN_STYLE_DEFAULTS.backgroundColor,
    primaryColor: urlPrimaryColor ?? YARDZEN_STYLE_DEFAULTS.primaryColor,
    textColor: urlTextColor ?? YARDZEN_STYLE_DEFAULTS.textColor,
    hideEventTypeDetails: false,
    hideLandingPageDetails: false,
  };
}

export const CalendlyPopup = ({
  url,
  prefillUserData,
  open,
  setOpen,
  onEventScheduled,
}: CalendlyPopupProps) => {
  useCalendlyEventListener({
    onEventScheduled: async (ev: EventScheduledEvent) => {
      onEventScheduled?.(ev?.data?.payload?.event?.uri);
    },
  });

  const prefillData = prefillUserData
    ? {
        email: prefillUserData.email || undefined,
        phone: prefillUserData.phone || undefined,
        firstName: prefillUserData.firstName || undefined,
        lastName: prefillUserData.lastName || undefined,
        name:
          prefillUserData.firstName && prefillUserData.lastName
            ? `${prefillUserData.firstName} ${prefillUserData.lastName}`
            : undefined,
      }
    : undefined;

  const pageSettings = buildPageSettings(url);

  return (
    <PopupModal
      url={url}
      rootElement={document.getElementById("root")!}
      onModalClose={() => setOpen(false)}
      pageSettings={pageSettings}
      prefill={prefillData}
      open={open}
    ></PopupModal>
  );
};
